/**
 * IndexedDB utility functions for storing and retrieving data
 * Similar to cookies but with larger storage capacity and better performance
 */

// Database configuration
const DB_NAME = 'linqops_db';
const DB_VERSION = 1;
const STORE_NAME = 'user_data';

// Check if we're running in a browser environment
const isBrowser = typeof window !== 'undefined';

/**
 * Deletes and recreates linqops_db at DB_VERSION.
 * Used to recover from a stale DB left at a higher version (e.g. v2) by a
 * previously shipped (and since reverted) migration.
 */
const recreateDB = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    const deleteRequest = window.indexedDB.deleteDatabase(DB_NAME);

    deleteRequest.onsuccess = () => {
      const createRequest = window.indexedDB.open(DB_NAME, DB_VERSION);

      createRequest.onsuccess = () => {
        createRequest.result.close();
        resolve();
      };

      createRequest.onerror = () => {
        reject(createRequest.error ?? new Error('Error recreating IndexedDB'));
      };

      createRequest.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: 'key' });
        }
      };
    };

    deleteRequest.onerror = () => {
      reject(deleteRequest.error ?? new Error('Error deleting IndexedDB'));
    };

    deleteRequest.onblocked = () => {
      // Another tab still has the DB open; resolve anyway, the reactive
      // VersionError handling in openDB() will catch any remaining issue.
      resolve();
    };
  });
};

/**
 * Checks the currently stored linqops_db version and, if it is greater than
 * DB_VERSION (e.g. left at v2 by a previously shipped and since reverted
 * migration), deletes and recreates the database at DB_VERSION.
 * Opening a DB at a version lower than its stored version throws a
 * VersionError, so this must run before we open at DB_VERSION.
 */
const checkAndFixDBVersion = (): Promise<void> => {
  return new Promise((resolve) => {
    const request = window.indexedDB.open(DB_NAME);

    // If linqops_db doesn't exist yet, this version-less open implicitly
    // creates it at version 1 and fires onupgradeneeded. Without handling
    // it here, the DB would be created with no object stores, causing a
    // NotFoundError later when getItem/setItem try to open a transaction.
    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'key' });
      }
    };

    request.onsuccess = async (event) => {
      const currentDB = (event.target as IDBOpenDBRequest).result;
      const currentVersion = currentDB.version;
      // Detect DBs stuck without the store (e.g. an earlier session hit the
      // bare-open NotFoundError bug before onupgradeneeded was handled here).
      // Same-version opens never retrigger onupgradeneeded, so this has to
      // be checked explicitly and recovered via recreateDB().
      const isMissingStore = !currentDB.objectStoreNames.contains(STORE_NAME);
      currentDB.close();

      if (currentVersion > DB_VERSION || isMissingStore) {
        console.log(`[IndexedDB] Recreating ${DB_NAME} (v${currentVersion}, missingStore=${isMissingStore})...`);
        try {
          await recreateDB();
          console.log('[IndexedDB] Database recreated successfully');
        } catch (error) {
          console.error('[IndexedDB] Error recreating database:', error);
        }
      }

      resolve();
    };

    request.onerror = () => {
      // Non-fatal: fall through and let openDB()'s normal open attempt run.
      resolve();
    };
  });
};

// Ensures the version check/fix runs only once per page session.
let dbVersionCheckPromise: Promise<void> | null = null;
const ensureDBVersionIsValid = (): Promise<void> => {
  if (!dbVersionCheckPromise) {
    dbVersionCheckPromise = checkAndFixDBVersion();
  }
  return dbVersionCheckPromise;
};

/**
 * Opens a connection to the IndexedDB database
 * @returns Promise with the database connection
 */
const openDB = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    // Check if we're in a browser environment
    if (!isBrowser) {
      reject(new Error('IndexedDB is only available in browser environments'));
      return;
    }
    
    // Check if IndexedDB is available
    if (!window.indexedDB) {
      reject(new Error('IndexedDB is not available in this browser'));
      return;
    }

    const open = () => {
      const request = window.indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = (event) => {
        const error = (event.target as IDBOpenDBRequest).error;

        // Fallback safety net: if the proactive version check above missed a
        // race (e.g. another tab bumped the version concurrently), recreate
        // the DB reactively here instead of failing outright.
        if (error && error.name === 'VersionError') {
          recreateDB()
            .then(() => {
              const retryRequest = window.indexedDB.open(DB_NAME, DB_VERSION);
              retryRequest.onsuccess = (e) => resolve((e.target as IDBOpenDBRequest).result);
              retryRequest.onerror = () => reject(new Error('Error opening IndexedDB after recreating it'));
              retryRequest.onupgradeneeded = (e) => {
                const db = (e.target as IDBOpenDBRequest).result;
                if (!db.objectStoreNames.contains(STORE_NAME)) {
                  db.createObjectStore(STORE_NAME, { keyPath: 'key' });
                }
              };
            })
            .catch(() => reject(new Error('Error opening IndexedDB')));
          return;
        }

        reject(new Error('Error opening IndexedDB'));
      };

      request.onsuccess = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        resolve(db);
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        
        // Create object store if it doesn't exist
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: 'key' });
        }
      };
    };

    ensureDBVersionIsValid().then(open);
  });
};

/**
 * Sets an item in the IndexedDB store
 * @param key The key to store the data under
 * @param value The value to store
 * @returns Promise that resolves when the data is stored
 */
export const setItem = async (key: string, value: any): Promise<void> => {
  // Check if we're in a browser environment
  if (!isBrowser) {
    console.warn('IndexedDB is only available in browser environments');
    return Promise.resolve();
  }
  
  try {
    const db = await openDB();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      
      const request = store.put({ key, value, timestamp: Date.now() });
      
      request.onerror = () => {
        reject(new Error(`Error storing data for key: ${key}`));
      };
      
      request.onsuccess = () => {
        resolve();
      };
      
      transaction.oncomplete = () => {
        db.close();
      };
    });
  } catch (error) {
    console.error('IndexedDB setItem error:', error);
    return Promise.resolve(); // Fail silently in production
  }
};

/**
 * Gets an item from the IndexedDB store
 * @param key The key to retrieve data for
 * @returns Promise with the retrieved value, or null if not found
 */
export const getItem = async <T = any>(key: string): Promise<T | null> => {
  // Check if we're in a browser environment
  if (!isBrowser) {
    console.warn('IndexedDB is only available in browser environments');
    return Promise.resolve(null);
  }
  
  try {
    const db = await openDB();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      
      const request = store.get(key);
      
      request.onsuccess = () => {
        const result = request.result;
        resolve(result ? result.value : null);
      };
      
      request.onerror = () => {
        reject(new Error(`Failed to retrieve data for key: ${key}`));
      };
      
      transaction.oncomplete = () => {
        db.close();
      };
    });
  } catch (error) {
    console.error('IndexedDB getItem error:', error);
    return Promise.resolve(null); // Fail silently in production
  }
};

/**
 * Removes an item from the IndexedDB store
 * @param key The key to remove
 * @returns Promise that resolves when the item is removed
 */
export const removeItem = async (key: string): Promise<void> => {
  // Check if we're in a browser environment
  if (!isBrowser) {
    console.warn('IndexedDB is only available in browser environments');
    return Promise.resolve();
  }
  
  try {
    const db = await openDB();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      
      const request = store.delete(key);
      
      request.onsuccess = () => {
        resolve();
      };
      
      request.onerror = () => {
        reject(new Error(`Failed to remove data for key: ${key}`));
      };
      
      transaction.oncomplete = () => {
        db.close();
      };
    });
  } catch (error) {
    console.error('IndexedDB removeItem error:', error);
    return Promise.resolve(); // Fail silently in production
  }
};

/**
 * Clears all items from the IndexedDB store
 * @returns Promise that resolves when all items are cleared
 */
export const clearAll = async (): Promise<void> => {
  // Check if we're in a browser environment
  if (!isBrowser) {
    return Promise.resolve(); // Fail silently in production
  }
  
  try {
    const db = await openDB();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      
      const request = store.clear();
      
      request.onsuccess = () => {
        resolve();
      };
      
      request.onerror = () => {
        reject(new Error('Failed to clear all data'));
      };
      
      transaction.oncomplete = () => {
        db.close();
      };
    });
  } catch (error) {
    console.error('IndexedDB clearAll error:', error);
    return Promise.resolve(); // Fail silently in production
  }
};

/**
 * Gets every item whose key starts with the given prefix.
 *
 * WHY THIS EXISTS:
 * prefStore.ts stores page preferences as rows in the same `user_data`
 * store (keys like "pref:Employee", "pref:Timecard") instead of a separate
 * object store. Hydrating the preference cache at boot needs ONLY the
 * preference rows — reading the whole store would also pull in the large
 * chat caches (last_*_messages_data) for no benefit.
 *
 * HOW IT WORKS:
 * IDBKeyRange.bound(prefix, prefix + '\uffff') selects exactly the keys that
 * begin with the prefix ('\uffff' is the largest character, so it is a
 * standard prefix-scan trick). A single getAll() on that range performs one
 * fast cursor pass and skips every unrelated row.
 *
 * RETURNS:
 * A plain object mapping { fullKey: value } (e.g. "pref:Employee" -> prefs).
 * Returns {} when IndexedDB is unavailable or the read fails — same
 * fail-silently style as getItem, so a storage problem never crashes a page.
 *
 * @param prefix The key prefix to match (e.g. 'pref:')
 */
export const getItemsByPrefix = async <T = any>(
  prefix: string
): Promise<Record<string, T>> => {
  if (!isBrowser) {
    console.warn('IndexedDB is only available in browser environments');
    return Promise.resolve({});
  }

  try {
    // openDB() runs the proactive version check first, so this helper is
    // protected by the same stale-version recovery as every other call.
    const db = await openDB();

    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);

      // Key-range scan: only rows whose key starts with `prefix`.
      const range = IDBKeyRange.bound(prefix, prefix + '\uffff');
      const request = store.getAll(range);

      request.onsuccess = () => {
        const rows = request.result as Array<{ key: string; value: T }>;
        const result: Record<string, T> = {};
        for (const row of rows) {
          result[row.key] = row.value;
        }
        resolve(result);
      };

      request.onerror = () => {
        reject(new Error(`Failed to retrieve items with prefix: ${prefix}`));
      };

      transaction.oncomplete = () => {
        db.close();
      };
    });
  } catch (error) {
    console.error('IndexedDB getItemsByPrefix error:', error);
    return Promise.resolve({}); // Fail silently in production
  }
};

/**
 * Subscription data interface
 * Stores plan access information in IndexedDB for client-side checks
 */
export interface SubscriptionData {
  subscriptionPlanId: string;
  subscriptionPlanName: string;
  accessiblePaths: string[];  // Paths accessible to this plan (covers both UI pages and API endpoints)
}

export interface EmployeeData {
  id: string;
}

/**
 * Interface for cached message data with metadata
 * Stores message preview, timestamp, and unread status to prevent flickering
 */
export interface CachedChatMessageItem {
  preview: string;
  timestamp: number | null;
  unread: boolean;
}

/**
 * Interface for the last messages cache
 * Key is employeeId, value can be the old string format (legacy) or new object format
 * Supports backward compatibility with existing string-only cache
 */
export interface LastMessagesData {
  [employeeId: string]: string | CachedChatMessageItem;
}

/**
 * Sets subscription data in IndexedDB
 * @param data The subscription data to store
 */
export const setSubscriptionData = async (data: SubscriptionData): Promise<void> => {
  return setItem('subscription_data', data);
};

/**
 * Gets subscription data from IndexedDB
 * @returns Promise with the subscription data, or null if not found
 */
export const getSubscriptionData = async (): Promise<SubscriptionData | null> => {
  return getItem<SubscriptionData>('subscription_data');
};

/**
 * Removes subscription data from IndexedDB
 */
export const removeSubscriptionData = async (): Promise<void> => {
  return removeItem('subscription_data');
};

export const setEmployeeData = async (data: EmployeeData): Promise<void> => {
  return setItem('employee_data', data);
};

export const getEmployeeData = async (): Promise<EmployeeData | null> => {
  return getItem<EmployeeData>('employee_data');
};

export const removeEmployeeData = async (): Promise<void> => {
  return removeItem('employee_data');
};

export const setLastDispatchMessagesData = async (data: LastMessagesData): Promise<void> => {
  return setItem('last_dispatch_messages_data', data);
};

export const getLastDispatchMessagesData = async (): Promise<LastMessagesData | null> => {
  return getItem<LastMessagesData>('last_dispatch_messages_data');
};

export const setLastHRMessagesData = async (data: LastMessagesData): Promise<void> => {
  return setItem('last_hr_messages_data', data);
};

export const getLastHRMessagesData = async (): Promise<LastMessagesData | null> => {
  return getItem<LastMessagesData>('last_hr_messages_data');
};

export const setLastAdminSupportMessagesData = async (data: LastMessagesData): Promise<void> => {
  return setItem('last_admin_support_messages_data', data);
};

export const getLastAdminSupportMessagesData = async (): Promise<LastMessagesData | null> => {
  return getItem<LastMessagesData>('last_admin_support_messages_data');
};